import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, ShareIcon, LinkIcon, CheckIcon } from '@heroicons/react/24/outline';
import { api } from '../config/api';
import { MarkdownRenderer } from '../components/MarkdownRenderer';
import KnowledgeGraphBackground from '../components/landing/KnowledgeGraphBackground';
import { useAuth, SupportedLanguage } from '../contexts/AuthContext';
import { LanguageToggle } from '../components/common/LanguageToggle';

const _PLAYBOOK_LANG_DISPLAY: Record<string, string> = {
  hindi: 'हिन्दी',
  gujarati: 'ગુજરાતી',
  marathi: 'मराठी',
};

export default function PlaybookView() {
  const { noteId } = useParams<{ noteId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const shareRef = useRef<HTMLDivElement>(null);
  const [markdown, setMarkdown] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copied, setCopied] = useState(false);
  // EN-toggle override: null = use user.language; 'english' = force English.
  const [playbookLangOverride, setPlaybookLangOverride] = useState<SupportedLanguage | null>(null);

  // Resolve effective target lang: override > user pref > english.
  const effectiveLang: string = playbookLangOverride
    || (user?.language && user.language !== 'english' ? user.language : 'english');
  const userLangDisplay = effectiveLang !== 'english'
    ? _PLAYBOOK_LANG_DISPLAY[effectiveLang]
    : null;

  useEffect(() => {
    if (!noteId) return;

    const fetchContent = async () => {
      setLoading(true);
      try {
        // Always pass the *effective* lang explicitly — override (when EN toggle
        // is on) or user.language. Backend would auto-detect from JWT anyway,
        // but explicit avoids surprises and supports the toggle's "force English"
        // behavior even when the user has Hindi pref.
        const params = new URLSearchParams();
        params.set('lang', effectiveLang);
        const res = await api.get(`/api/smart-notes/${noteId}/public?${params.toString()}`);

        if (res.data.content_json?.markdown) {
          setMarkdown(res.data.content_json.markdown);
        } else {
          setError('No content available');
        }
      } catch {
        setError('Failed to load playbook');
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [noteId, effectiveLang]);

  // Close share menu on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (shareRef.current && !shareRef.current.contains(e.target as Node)) {
        setShowShareMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareText = 'Check out my playbook';

  const handleWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(`${shareText}: ${shareUrl}`)}`, '_blank');
    setShowShareMenu(false);
  };

  const handleTelegram = () => {
    window.open(`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`, '_blank');
    setShowShareMenu(false);
  };

  const handleTwitter = () => {
    window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`, '_blank');
    setShowShareMenu(false);
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    setShowShareMenu(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#fdfcfa' }}>
        <div className="flex flex-col items-center gap-4 text-center max-w-sm px-6">
          <div className="animate-spin h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full" />
          {userLangDisplay ? (
            <>
              <p className="text-base font-medium text-slate-900">
                Translating to {userLangDisplay}...
              </p>
              <p className="text-xs text-slate-500">
                First read takes a moment — next time is instant ✨
              </p>
            </>
          ) : (
            <p className="text-sm text-slate-500">Loading your Playbook...</p>
          )}
        </div>
      </div>
    );
  }

  if (error || !markdown) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#fdfcfa' }}>
        <p className="text-black">{error || 'No content'}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#fdfcfa' }}>
      <link href="https://fonts.googleapis.com/css2?family=Caveat:wght@400;600;700&display=swap" rel="stylesheet" />

      <style>{`
        .playbook-content,
        .playbook-content p,
        .playbook-content li,
        .playbook-content strong,
        .playbook-content h1,
        .playbook-content h2,
        .playbook-content th,
        .playbook-content td {
          color: #000 !important;
        }
        .playbook-content h3 {
          color: #2563eb !important;
        }
        .playbook-content em {
          color: #2563eb !important;
        }
        .playbook-content blockquote,
        .playbook-content blockquote p,
        .playbook-content blockquote em {
          font-family: 'Caveat', cursive !important;
          font-size: 1.25rem !important;
          line-height: 1.6 !important;
          color: #dc2626 !important;
          border-left-color: #dc2626 !important;
          background: transparent !important;
          font-weight: 600 !important;
        }
        .playbook-content blockquote strong {
          color: #991b1b !important;
          font-weight: 700 !important;
        }
      `}</style>

      <div className="print:hidden">
        <KnowledgeGraphBackground />
      </div>

      {/* Top bar */}
      <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-sm border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate('/playbook')}
            className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 transition-colors"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back
          </button>

          <div className="flex items-center gap-3">
            <LanguageToggle
              overrideLang={playbookLangOverride}
              onToggle={setPlaybookLangOverride}
              isDarkMode={false}
              size="sm"
            />

          {/* Share dropdown */}
          <div ref={shareRef} className="relative">
            <button
              onClick={() => setShowShareMenu(!showShareMenu)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors"
            >
              <ShareIcon className="h-4 w-4" />
              Share
            </button>

            {showShareMenu && (
              <div className="absolute right-0 mt-2 w-48 rounded-xl bg-white border border-slate-200 shadow-lg overflow-hidden z-20">
                <button
                  onClick={handleWhatsApp}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-slate-50 transition-colors"
                >
                  <span className="w-2 h-2 rounded-full bg-[#25D366]" />
                  <span className="text-slate-700">WhatsApp</span>
                </button>
                <button
                  onClick={handleTelegram}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-slate-50 transition-colors"
                >
                  <span className="w-2 h-2 rounded-full bg-[#0088cc]" />
                  <span className="text-slate-700">Telegram</span>
                </button>
                <button
                  onClick={handleTwitter}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-slate-50 transition-colors"
                >
                  <span className="w-2 h-2 rounded-full bg-black" />
                  <span className="text-slate-700">Twitter / X</span>
                </button>
                <div className="border-t border-slate-100" />
                <button
                  onClick={handleCopyLink}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-slate-50 transition-colors"
                >
                  {copied
                    ? <CheckIcon className="h-4 w-4 text-green-500" />
                    : <LinkIcon className="h-4 w-4 text-slate-400" />
                  }
                  <span className="text-slate-700">{copied ? 'Copied!' : 'Copy link'}</span>
                </button>
              </div>
            )}
          </div>
          </div>
        </div>
      </div>

      {/* Markdown content */}
      <div className="relative z-1 max-w-3xl mx-auto px-6 py-8">
        <div className="playbook-content prose prose-sm md:prose-base prose-slate max-w-none
          prose-h1:text-3xl prose-h1:font-bold prose-h1:text-black prose-h1:mb-1
          prose-h3:text-lg prose-h3:text-blue-600 prose-h3:font-medium prose-h3:mt-0
          prose-h2:text-xl prose-h2:font-bold prose-h2:text-black prose-h2:border-b-2 prose-h2:border-blue-500 prose-h2:pb-2 prose-h2:mt-10
          prose-strong:text-black
          prose-blockquote:py-2 prose-blockquote:px-4 prose-blockquote:rounded-r-lg prose-blockquote:not-italic
          prose-hr:border-slate-200
          prose-p:text-black prose-p:leading-relaxed
          prose-li:text-black
          prose-em:text-blue-600
        ">
          <MarkdownRenderer content={markdown} />
        </div>
      </div>
    </div>
  );
}
